/**
 * ExecutionsTab.tsx
 * Executions tab — matches the look and feel of RequirementTestWorkspace (Test Cases tab).
 * Left panel: directory tree of Playwright runs with Passed/Failed sub-folders.
 * Right panel: test result cards with Preview popup.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Spinner from 'react-bootstrap/Spinner';
import Tooltip from 'react-bootstrap/Tooltip';
import {
  ArrowClockwise,
  ChevronDown,
  ChevronRight,
  EyeFill,
  Folder2Open,
  FolderFill,
  InfoCircle,
  PencilFill,
  PlayFill,
  Search,
} from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../../api/config';
import { TEST_EXECUTION_DISABLED_MESSAGE } from '../../constants/featureMessages';
import { useActiveFeatureFlags } from '../../hooks/useActiveFeatureFlags';
import type { ProjectWithRepository } from '../../types/project';
import ScriptArgumentsDialog from '../ScriptArgumentsDialog';
import ExecutionTestPopup, { ActionUpdateSummary, ExecutionTestResult, variantOrdinal } from './ExecutionTestPopup';
import RunConfigModal, { RunConfig } from './RunConfigModal';
import '../requirements/RequirementTestWorkspace.css';
import './ExecutionsTab.css';

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
interface PlaywrightJob {
  id: number;
  status: string;
  base_url: string | null;
  run_name: string | null;
  run_id: string | null;
  artifact_id?: number | null;
  source_version_number?: number | null;
  started_at?: string | null;
  created_at: string;
  completed_at: string | null;
  scope_label?: string | null;
  total_tests?: number | null;
  passed?: number | null;
  failed?: number | null;
  partially_passed?: number | null;
  pass_rate?: number | null;
  run_config?: RunConfig | null;
  ado_sync?: AdoSyncRun | null;
  /** Live partial summary streamed from execution checkpoints while the job runs. */
  live_results?: RunSummary | null;
}

interface AdoSyncResult {
  test_key: string;
  ado_work_item_id?: number | null;
  ado_test_point_id?: number | null;
  ado_test_result_id?: number | null;
  outcome?: string | null;
  status: string;
  message?: string | null;
  screenshot_attached?: boolean;
}

interface AdoSyncRun {
  id?: number | null;
  ado_test_run_id?: number | null;
  status: string;
  ado_run_url?: string | null;
  error_message?: string | null;
  total_count?: number;
  processed_count?: number;
  synced_count?: number;
  skipped_count?: number;
  failed_count?: number;
  current_test_key?: string | null;
  current_action?: string | null;
  message?: string | null;
  results?: AdoSyncResult[];
}

type RunDialogScope = { title: string; testKeys: string[]; artifactId: number | null };

type VariantRunTest = {
  testKey: string;
  title: string;
  variantCount: number;
};

type VariantRunConfirmation = {
  action: 'single' | 'dialog';
  title: string;
  testKeys: string[];
  artifactId: number | null;
  variantTests: VariantRunTest[];
  singleTestKey?: string;
};

interface RunSummary {
  total_tests?: number;
  passed?: number;
  failed?: number;
  pass_rate?: number;
  test_results?: ExecutionTestResult[];
  result_history?: Record<string, Array<Record<string, any>>>;
  [key: string]: any;
}

type FolderFilter = 'all' | 'passed' | 'partially_passed' | 'failed';

const ACTIVE_EXECUTION_POLL_MS = 10000;
const ACTIVE_SUMMARY_REFRESH_EVERY = 3;

interface Props {
  projectId: number;
  initialJobId?: number | null;
}

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatVersion(value?: number | null): string {
  if (value == null) return '—';
  return `v${value}`;
}

function formatActionUpdateSummary(summaries: ActionUpdateSummary[]): string | null {
  if (!summaries.length) return null;
  // Guard against the same step arriving from more than one source, which used
  // to render as "Steps 3, 3 actions updated in v2".
  const seenSteps = new Set<number>();
  const sorted = summaries
    .filter(item => {
      if (seenSteps.has(item.step_number)) return false;
      seenSteps.add(item.step_number);
      return true;
    })
    .sort((a, b) => a.step_number - b.step_number);
  const version = sorted[0]?.version_number;
  if (sorted.length === 1) {
    return sorted[0].label || `Step ${sorted[0].step_number} action updated in v${version}`;
  }
  const steps = sorted.map(item => item.step_number).join(', ');
  return `Steps ${steps} actions updated in v${version}`;
}

/** Format a duration in milliseconds to a human-readable string, e.g. "3m 24s" or "45s" */
function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function jobElapsedDurationMs(job?: Pick<PlaywrightJob, 'started_at' | 'completed_at'> | null): number | null {
  if (!job?.started_at || !job.completed_at) return null;

  const elapsed = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
  return Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : null;
}

function resolvePass(t: ExecutionTestResult): boolean | null {
  if (t.pass !== undefined) return t.pass;
  const s = t.status?.toLowerCase();
  if (s === 'passed' || s === 'pass') return true;
  if (s === 'failed' || s === 'fail') return false;
  return null;
}

function testStatusPill(t: ExecutionTestResult): string {
  const p = resolvePass(t);
  if (p === true) return 'passed';
  if (p === false) return 'failed';
  return 'never_run';
}

function executionStatus(t: ExecutionTestResult): FolderFilter | 'never_run' {
  const stats = getVariantStats(t);
  if (stats.total > 0 && stats.passed > 0 && stats.failed > 0) return 'partially_passed';
  if (stats.total > 0 && stats.failed > 0) return 'failed';
  if (stats.total > 0 && stats.passed > 0 && stats.unknown === 0) return 'passed';
  if (stats.total > 0) return 'never_run';
  return testStatusPill(t) as FolderFilter | 'never_run';
}

function testStatusLabel(t: ExecutionTestResult): string {
  const p = resolvePass(t);
  if (p === true) return 'Passed';
  if (p === false) return 'Failed';
  return 'Unknown';
}

function isUnsuccessfulExecutionStatus(status?: string | null): boolean {
  const normalized = (status || '').toLowerCase();
  return normalized === 'failed' || normalized === 'cancelled' || normalized === 'canceled';
}

function keySetHasTestOrVariant(keys: Set<string>, testKey: string | null): boolean {
  if (!testKey) return false;
  if (keys.has(testKey)) return true;
  const variantPrefix = `${testKey}::`;
  for (const key of Array.from(keys)) {
    if (key.startsWith(variantPrefix)) return true;
  }
  return false;
}

function removeKeyFromSet(keys: Set<string>, key: string | null): Set<string> {
  const next = new Set(keys);
  if (key) next.delete(key);
  return next;
}

type VariantStats = {
  total: number;
  passed: number;
  failed: number;
  unknown: number;
};

type FailureClassification = 'script_failure' | 'application_failure';

type FailureVersionSummary = {
  versionLabel: string;
  scriptVariants: string[];
  applicationVariants: string[];
};

function isFailureClassification(value: unknown): value is FailureClassification {
  return value === 'script_failure' || value === 'application_failure';
}

function failureClassificationSummary(
  test: ExecutionTestResult,
  latestClassifications: Record<string, string>,
): FailureVersionSummary[] {
  const historyEntries = (test.result_history || [])
    .filter(entry => !!entry?.result)
    .slice()
    .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  const historicalResults = historyEntries.map(entry => entry.result!);

  const versions = [...historicalResults, test];
  const hasVariants = versions.some(result => Array.isArray(result.variants) && result.variants.length > 0);

  // Every snapshot stores the complete variant list, even when the version was
  // produced by a single-variant re-run. Walking oldest → newest lets each
  // version see only the runs that preceded it, so the untouched carry-over
  // variants are attributed to the version that actually executed them.
  const runIdsSoFar = new Set<string>();
  const variantsByVersion = versions.map((result, versionIndex) => {
    const allVariantResults = Array.isArray(result.variants) && result.variants.length > 0
      ? result.variants
      : [result];
    const scoped = runIdsSoFar.size === 0 || allVariantResults.length <= 1
      ? allVariantResults
      : (() => {
        const reExecuted = allVariantResults.filter(variant => {
          const runId = String(variant.run_id || '').trim();
          return !!runId && !runIdsSoFar.has(runId);
        });
        return reExecuted.length > 0 ? reExecuted : allVariantResults;
      })();
    [
      historyEntries[versionIndex]?.run_id,
      result.run_id,
      ...allVariantResults.map(variant => variant.run_id),
    ].forEach(candidate => {
      const text = String(candidate || '').trim();
      if (text) runIdsSoFar.add(text);
    });
    return scoped;
  });

  return versions.reduce<FailureVersionSummary[]>((summaries, result, versionIndex) => {
    const scriptVariants: string[] = [];
    const applicationVariants: string[] = [];
    const variantResults = variantsByVersion[versionIndex];

    variantResults.forEach((variant, variantIndex) => {
      if (resolvePass(variant) !== false) return;
      const executionKey = String(variant.execution_key || '').trim();
      const classification = variant.failure_classification
        || (result === test ? latestClassifications[executionKey || String(test.test_case_key || '')] : undefined);
      if (!isFailureClassification(classification)) return;

      // `variant_index` is 0-based (it is the position sent with a re-run
      // request), so rendering it directly labelled the re-run variant one
      // number too low and produced duplicates like "Variant 1, Variant 1".
      // Derive the label from the variant's own id instead.
      const variantLabel = hasVariants
        ? `Variant ${variantOrdinal(variant, variantIndex)}`
        : 'Test';
      if (classification === 'script_failure') scriptVariants.push(variantLabel);
      else applicationVariants.push(variantLabel);
    });

    if (scriptVariants.length === 0 && applicationVariants.length === 0) return summaries;
    summaries.push({
      versionLabel: `Version ${versionIndex + 1}`,
      scriptVariants,
      applicationVariants,
    });
    return summaries;
  }, []);
}

function getVariantStats(test: ExecutionTestResult): VariantStats {
  const variants = Array.isArray(test.variants) ? test.variants : [];
  const total = typeof test.variant_count === 'number' ? test.variant_count : variants.length;
  const passed = typeof test.passed_variants === 'number'
    ? test.passed_variants
    : variants.filter(variant => resolvePass(variant) === true).length;
  const failed = typeof test.failed_variants === 'number'
    ? test.failed_variants
    : variants.filter(variant => resolvePass(variant) === false).length;
  const unknown = Math.max(0, total - passed - failed);
  return { total, passed, failed, unknown };
}

function executionResultKey(result: ExecutionTestResult): string {
  const explicit = String(result.test_case_key || '').trim();
  if (explicit) return explicit;
  const executionKey = String(result.execution_key || '').trim();
  if (executionKey.includes('::')) return executionKey.split('::', 1)[0];
  return String(result.execution_key || result.name || result.title || '').trim();
}

function isVariantLikeResult(parentKey: string, result: ExecutionTestResult): boolean {
  const executionKey = String(result.execution_key || '').trim();
  return Boolean(
    result.variant_id
    || result.dataset_id
    || result.dataset_name
    || result.data
    || result.synthetic_data
    || (executionKey && executionKey !== parentKey && executionKey.startsWith(`${parentKey}::`))
  );
}

function variantHistoryKeys(parentKey: string, variant: ExecutionTestResult): string[] {
  return [
    variant.execution_key,
    variant.variant_id ? `${parentKey}::${variant.variant_id}` : null,
    variant.evidence_key,
  ].filter((value): value is string => !!value);
}

function attachResultHistory(
  parentKey: string,
  variant: ExecutionTestResult,
  history: Record<string, Array<Record<string, any>>>,
): ExecutionTestResult {
  const entries: Array<Record<string, any>> = [];
  variantHistoryKeys(parentKey, variant).forEach(key => {
    const list = history[key] || [];
    list.forEach(item => {
      if (!entries.some(existing => existing.id && existing.id === item.id)) entries.push(item);
    });
  });
  return entries.length > 0 ? { ...variant, result_history: entries as ExecutionTestResult['result_history'] } : variant;
}

function normalizeExecutionTests(summary: RunSummary | null): ExecutionTestResult[] {
  const raw = summary?.test_results ?? [];
  const history = summary?.result_history ?? {};
  const grouped = new Map<string, ExecutionTestResult[]>();
  const parentByKey = new Map<string, ExecutionTestResult>();

  raw.forEach(result => {
    if (!result || typeof result !== 'object') return;
    const key = executionResultKey(result);
    if (!key) return;
    if (!parentByKey.has(key)) parentByKey.set(key, result);
    if (Array.isArray(result.variants) && result.variants.length > 0) {
      const normalizedVariants = result.variants.map(variant => attachResultHistory(key, {
        ...variant,
        test_case_key: variant.test_case_key || result.test_case_key || key,
        title: variant.title || result.title,
        name: variant.name || result.name,
      }, history));
      grouped.set(key, [...(grouped.get(key) || []), ...normalizedVariants]);
    } else if (isVariantLikeResult(key, result)) {
      grouped.set(key, [...(grouped.get(key) || []), attachResultHistory(key, {
        ...result,
        test_case_key: result.test_case_key || key,
      }, history)]);
    }
  });

  const orderedKeys = Array.from(parentByKey.keys());
  grouped.forEach((_, key) => {
    if (!orderedKeys.includes(key)) orderedKeys.push(key);
  });

  return orderedKeys.map(key => {
    const variants = grouped.get(key) || [];
    const parent = parentByKey.get(key) || variants[0] || {};
    // Parent-level snapshots represent complete historical versions (all
    // variants), so retain them separately from each variant's own history.
    const parentHistory = history[key] || [];
    const parentWithHistory = parentHistory.length > 0
      ? { ...parent, result_history: parentHistory as ExecutionTestResult['result_history'] }
      : attachResultHistory(key, parent, history);
    const uniqueVariants: ExecutionTestResult[] = [];
    const seen = new Set<string>();
    variants.forEach((variant, index) => {
      const vKey = variant.execution_key || variant.variant_id || variant.evidence_key || `${key}:${index}`;
      if (seen.has(vKey)) return;
      seen.add(vKey);
      uniqueVariants.push(variant);
    });
    if (uniqueVariants.length <= 1 && !(Array.isArray(parent.variants) && parent.variants.length > 0)) {
      return { ...parentWithHistory, result_history: parentWithHistory.result_history };
    }
    const passedVariants = uniqueVariants.filter(variant => resolvePass(variant) === true).length;
    const failedVariants = uniqueVariants.filter(variant => resolvePass(variant) === false).length;
    return {
      ...parentWithHistory,
      test_case_key: parentWithHistory.test_case_key || key,
      execution_key: parentWithHistory.test_case_key || key,
      variants: uniqueVariants,
      variant_count: uniqueVariants.length,
      passed_variants: passedVariants,
      failed_variants: failedVariants,
      pass: failedVariants === 0,
      status: failedVariants > 0 && passedVariants > 0 ? 'partially_passed' : failedVariants > 0 ? 'failed' : 'passed',
      duration_ms: uniqueVariants.reduce((sum, variant) => sum + Number(variant.duration_ms || 0), 0),
      error: uniqueVariants.find(variant => variant.error)?.error || parentWithHistory.error,
    };
  });
}

function formatVariantStatus(stats: VariantStats): string {
  const parts: string[] = [];
  if (stats.passed > 0) parts.push(`${stats.passed} Passed`);
  if (stats.failed > 0) parts.push(`${stats.failed} Failed`);
  if (stats.unknown > 0) parts.push(`${stats.unknown} Unknown`);
  return parts.length > 0 ? parts.join(', ') : 'No Variants';
}

function getAdoSyncProgress(sync: AdoSyncRun | null): number {
  if (!sync?.total_count) return 0;
  return Math.min(100, Math.round(((sync.processed_count ?? 0) / sync.total_count) * 100));
}

function isAdoSyncTerminal(status?: string | null): boolean {
  const normalized = (status || '').toLowerCase();
  return normalized !== '' && normalized !== 'syncing';
}

function getCardStatus(test: ExecutionTestResult): { pill: string; label: string } {
  const stats = getVariantStats(test);
  if (stats.total > 0) {
    if (stats.failed > 0 && stats.passed > 0) {
      return { pill: 'partially_passed', label: `Partially Passed (${formatVariantStatus(stats)})` };
    }
    if (stats.failed > 0) {
      return { pill: 'failed', label: formatVariantStatus(stats) };
    }
    if (stats.passed > 0 && stats.unknown === 0) {
      return { pill: 'passed', label: formatVariantStatus(stats) };
    }
    return { pill: 'default', label: formatVariantStatus(stats) };
  }

  return { pill: testStatusPill(test), label: testStatusLabel(test) };
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
export default function ExecutionsTab({ projectId, initialJobId = null }: Props): React.ReactElement {
  const {
    flags: featureFlags,
    loading: featureFlagsLoading,
    refresh: refreshFeatureFlags,
  } = useActiveFeatureFlags();
  const [jobs, setJobs] = useState<PlaywrightJob[]>([]);
  const [project, setProject] = useState<ProjectWithRepository | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [executionActionError, setExecutionActionError] = useState<string | null>(null);

  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJob, setSelectedJobDetail] = useState<PlaywrightJob | null>(null);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Which "sub-folder" is active in the left tree
  const [folderFilter, setFolderFilter] = useState<FolderFilter>('all');
  // Which run IDs are expanded in the tree
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Right panel filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTestKeys, setSelectedTestKeys] = useState<Set<string>>(new Set());

  // Popup
  const [previewTest, setPreviewTest] = useState<ExecutionTestResult | null>(null);

  // Run dialog
  const [runDialogScope, setRunDialogScope] = useState<RunDialogScope | null>(null);
  const [variantRunConfirmation, setVariantRunConfirmation] = useState<VariantRunConfirmation | null>(null);
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean;
    job: PlaywrightJob | null;
    value: string;
    saving: boolean;
    error: string | null;
  }>({ open: false, job: null, value: '', saving: false, error: null });

  // Keys that have been queued for re-run (shows badge until next jobs reload)
  const [rerunQueuedKeys, setRerunQueuedKeys] = useState<Set<string>>(new Set());
  const [rerunningKey, setRerunningKey] = useState<string | null>(null);
  // Test keys whose actions have been user-edited (permanent "Actions Updated" badge)
  const [editedTestKeys, setEditedTestKeys] = useState<Set<string>>(new Set());
  const [actionUpdateSummaries, setActionUpdateSummaries] = useState<Record<string, ActionUpdateSummary[]>>({});
  // Test keys that have been individually re-run (permanent "Re-ran" badge)
  const [rerunTestKeys, setRerunTestKeys] = useState<Set<string>>(new Set());
  // Failure classifications from last run: test_key → "script_failure" | "application_failure"
  const [failureClassifications, setFailureClassifications] = useState<Record<string, string>>({});
  // Re-run all loading state
  const [rerunAllLoading, setRerunAllLoading] = useState(false);
  // True between POST success and job appearing as running in the jobs list
  const [startingRun, setStartingRun] = useState(false);
  const [showRunConfig, setShowRunConfig] = useState(false);
  // Re-run name dialog
  const [rerunNameDialog, setRerunNameDialog] = useState(false);
  const [rerunNameValue, setRerunNameValue] = useState('');
  const [rerunMode, setRerunMode] = useState<string>('rerun');
  // Fresh run — opens full credentials dialog instead of simple name prompt
  const [freshRunDialog, setFreshRunDialog] = useState(false);
  const [adoSyncing, setAdoSyncing] = useState(false);
  const [adoSyncError, setAdoSyncError] = useState<string | null>(null);
  // Single-test rerun job ID being polled (set after popup Save & Re-run, cleared when job settles)
  const [rerunInProgressJobId, setRerunInProgressJobId] = useState<number | null>(null);
  const [rerunInProgressKey, setRerunInProgressKey] = useState<string | null>(null);
  const rerunPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activePollTickRef = useRef(0);
  const initialJobSelectionHandledRef = useRef<number | null>(null);
  const isAzureProject = (project?.project_type ?? '').toLowerCase() === 'azure_devops';
  const testExecutionActionDisabled = featureFlagsLoading || !featureFlags.test_execution;

  const ensureTestExecutionStartAllowed = useCallback(() => {
    if (featureFlagsLoading) {
      void refreshFeatureFlags();
      setExecutionActionError('Checking test execution access. Please try again in a moment.');
      return false;
    }

    if (!featureFlags.test_execution) {
      setExecutionActionError(TEST_EXECUTION_DISABLED_MESSAGE);
      return false;
    }

    setExecutionActionError(null);
    return true;
  }, [featureFlags.test_execution, featureFlagsLoading, refreshFeatureFlags]);

  useEffect(() => {
    let cancelled = false;

    const loadProject = async () => {
      try {
        const res = await apiClient.get<ProjectWithRepository>(
          API_ENDPOINTS.PROJECT_DETAIL(projectId)
        );
        if (!cancelled) setProject(res.data);
      } catch {
        if (!cancelled) setProject(null);
      }
    };

    void loadProject();

    return () => { cancelled = true; };
  }, [projectId]);

  /* ── Load jobs list ── */
  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    setJobsError(null);
    try {
      const res = await apiClient.get<PlaywrightJob[]>(
        API_ENDPOINTS.PLAYWRIGHT_EXECUTIONS(projectId)
      );
      setJobs(res.data);
      setExpandedIds(new Set(res.data.map((job) => job.id)));
    } catch (e: any) {
      setJobsError(e?.response?.data?.detail || e?.message || 'Failed to load executions');
    } finally {
      setLoadingJobs(false);
    }
  }, [projectId]);

  const loadExecutionDetail = useCallback(async (jobId: number) => {
    const res = await apiClient.get<PlaywrightJob>(
      API_ENDPOINTS.PLAYWRIGHT_EXECUTION_DETAIL(projectId, jobId)
    );
    return res.data;
  }, [projectId]);

  const loadExecutionSummary = useCallback(async (jobId: number, includeArtifactsTree = false) => {
    const res = await apiClient.get<{ summary: RunSummary }>(
      API_ENDPOINTS.PLAYWRIGHT_EXECUTION_SUMMARY(projectId, jobId),
      includeArtifactsTree ? { params: { include_artifacts_tree: true } } : undefined,
    );
    return res.data?.summary || null;
  }, [projectId]);

  const refreshResolvedActionMetadata = useCallback(async (artifactId?: number | null, jobId?: number | null) => {
    if (!artifactId) {
      setEditedTestKeys(new Set());
      setActionUpdateSummaries({});
      setRerunTestKeys(new Set());
      setFailureClassifications({});
      return;
    }
    try {
      const actRes = await apiClient.get<{
        edited_test_keys?: string[];
        rerun_test_keys?: string[];
        action_update_summaries?: Record<string, ActionUpdateSummary[]>;
      }>(
        API_ENDPOINTS.PLAYWRIGHT_RESOLVED_ACTIONS(projectId, artifactId, jobId || undefined)
      );
      setEditedTestKeys(new Set(actRes.data?.edited_test_keys ?? []));
      setActionUpdateSummaries(actRes.data?.action_update_summaries ?? {});
      setRerunTestKeys(new Set(actRes.data?.rerun_test_keys ?? []));
      setFailureClassifications((actRes.data as any)?.failure_classifications ?? {});
    } catch {
      setEditedTestKeys(new Set());
      setActionUpdateSummaries({});
      setRerunTestKeys(new Set());
      setFailureClassifications({});
    }
  }, [projectId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  /* ── Poll while any job is running/pending ── */
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'running' || j.status === 'pending');
    // Once we see an active job, the run has actually started — clear the "starting" banner
    if (hasActive) setStartingRun(false);
    if (hasActive && !pollingRef.current) {
      pollingRef.current = setInterval(async () => {
        try {
          activePollTickRef.current += 1;
          const res = await apiClient.get<PlaywrightJob[]>(API_ENDPOINTS.PLAYWRIGHT_EXECUTIONS(projectId));
          setJobs(res.data);
          const updatedSelected = res.data.find(j => j.id === selectedJobId);
          if (updatedSelected) {
            setSelectedJobDetail(updatedSelected);
            const hasNoTests = !summary || (summary.test_results ?? []).length === 0;
            try {
              const detailRes = await loadExecutionDetail(updatedSelected.id);
              setSelectedJobDetail(prev => prev ? { ...prev, ...detailRes } : detailRes);
              const shouldRefreshSummary =
                hasNoTests
                || updatedSelected.status === 'completed'
                || activePollTickRef.current % ACTIVE_SUMMARY_REFRESH_EVERY === 0;
              if (shouldRefreshSummary) {
                if (hasNoTests) setLoadingSummary(true);
                const refreshedSummary = await loadExecutionSummary(updatedSelected.id);
                setSummary(refreshedSummary);
              }
              if (updatedSelected.status === 'completed' && detailRes.artifact_id) {
                await refreshResolvedActionMetadata(detailRes.artifact_id, updatedSelected.id);
              }
            } finally {
              if (hasNoTests) setLoadingSummary(false);
            }
          }
          // Stop polling once all jobs are settled
          const stillActive = res.data.some(j => j.status === 'running' || j.status === 'pending');
          if (!stillActive && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } catch { /* silently ignore poll errors */ }
      }, ACTIVE_EXECUTION_POLL_MS);
    } else if (!hasActive && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, projectId, selectedJobId]);

  /* ── Poll a single-test rerun job directly (it's filtered from the jobs list) ── */
  useEffect(() => {
    if (!rerunInProgressJobId) {
      if (rerunPollRef.current) { clearInterval(rerunPollRef.current); rerunPollRef.current = null; }
      return;
    }
    rerunPollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get<{ status: string }>(
          API_ENDPOINTS.PLAYWRIGHT_EXECUTION_DETAIL(projectId, rerunInProgressJobId)
        );
        const normalizedStatus = (res.data?.status || '').toLowerCase();
        if (normalizedStatus !== 'pending' && normalizedStatus !== 'queued' && normalizedStatus !== 'running') {
          const finishedRerunKey = rerunInProgressKey;
          const rerunCompleted = normalizedStatus === 'completed' || normalizedStatus === 'succeeded' || normalizedStatus === 'success';
          // Rerun settled — stop polling and refresh the parent job's summary
          clearInterval(rerunPollRef.current!);
          rerunPollRef.current = null;
          setRerunInProgressJobId(null);
          setRerunInProgressKey(null);
          if (finishedRerunKey) {
            setRerunQueuedKeys(prev => removeKeyFromSet(prev, finishedRerunKey));
            if (rerunCompleted) {
              setRerunTestKeys(prev => new Set([...Array.from(prev), finishedRerunKey]));
            }
          }
          // Force-refresh: clear summary so selectJob's early-return guard is bypassed
          if (selectedJobId != null) {
            setSummary(null);
            try {
               const [detailRes, summaryRes] = await Promise.all([
                 loadExecutionDetail(selectedJobId),
                 loadExecutionSummary(selectedJobId),
               ]);
               setSummary(summaryRes);
               if (summaryRes && selectedJobId != null) {
                 setJobs(prev => prev.map(j =>
                   j.id === selectedJobId
                     ? { ...j, passed: summaryRes.passed ?? j.passed, failed: summaryRes.failed ?? j.failed }
                     : j
                 ));
               }
               setSelectedJobDetail(prev => prev ? { ...prev, ...detailRes } : detailRes);
               await refreshResolvedActionMetadata(detailRes.artifact_id, rerunInProgressJobId);
             } catch { /* silently ignore */ }
           }
          // The left folders use the jobs-list counts, so reload that list after
          // the parent result has been updated by the completed single-test rerun.
          await loadJobs();
         }
      } catch { /* silently ignore poll errors */ }
    }, 4000);
    return () => { if (rerunPollRef.current) { clearInterval(rerunPollRef.current); rerunPollRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunInProgressJobId, rerunInProgressKey, projectId, selectedJobId, refreshResolvedActionMetadata]);

  /* ── Auto-select most recent job once jobs are loaded ── */
  useEffect(() => {
    if (jobs.length === 0 || selectedJobId !== null) return;

    if (initialJobId && initialJobSelectionHandledRef.current !== initialJobId) {
      initialJobSelectionHandledRef.current = initialJobId;
      const requestedJob = jobs.find(j => j.id === initialJobId);
      if (requestedJob) {
        selectJob(requestedJob);
        return;
      }
    }

    // Prefer the most recent running/pending job so the user sees live progress,
    // otherwise fall back to the most recent completed job, then just jobs[0].
    const best =
      jobs.find(j => j.status === 'running' || j.status === 'pending') ??
      jobs.find(j => j.status === 'completed') ??
      jobs[0];
    if (best) selectJob(best);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, initialJobId, selectedJobId]);

  /* ── Load summary when job selected ── */
  const selectJob = useCallback(async (job: PlaywrightJob, filter: FolderFilter = 'all') => {
    setSelectedJobId(job.id);
    setSelectedJobDetail(job);
    setFolderFilter(filter);
    setSearch('');
    setStatusFilter('all');
    setAdoSyncError(null);

    if (summary && selectedJobId === job.id) return; // already loaded

    setSummary(null);
    setLoadingSummary(true);
    try {
      const [detailRes, summaryRes] = await Promise.allSettled([
        loadExecutionDetail(job.id),
        loadExecutionSummary(job.id),
      ]);
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value);
      }
      if (detailRes.status === 'fulfilled') {
        setSelectedJobDetail(prev => prev ? { ...prev, ...detailRes.value } : detailRes.value);
      }
      // Load edited/rerun keys and DeferredAction -> resolved-action badges.
      await refreshResolvedActionMetadata(job.artifact_id, job.id);
    } finally {
      setLoadingSummary(false);
    }
  }, [loadExecutionDetail, loadExecutionSummary, refreshResolvedActionMetadata, selectedJobId, summary]);

  const handleAdoSync = useCallback(async () => {
    if (!selectedJob || !isAzureProject) return;

    setAdoSyncing(true);
    setAdoSyncError(null);
    try {
      const response = await apiClient.post<AdoSyncRun>(
        API_ENDPOINTS.PLAYWRIGHT_ADO_SYNC(projectId, selectedJob.id)
      );
      setSelectedJobDetail(prev => prev ? { ...prev, ado_sync: response.data } : prev);
      setJobs(prev => prev.map(job => (
        job.id === selectedJob.id ? { ...job, ado_sync: response.data } : job
      )));
    } catch (err: any) {
      setAdoSyncError(err?.response?.data?.detail || err?.message || 'Failed to sync with Azure Test Plans');
    } finally {
      setAdoSyncing(false);
    }
  }, [isAzureProject, projectId, selectedJob]);

  useEffect(() => {
    if (!isAzureProject || !selectedJob?.id) return undefined;
    const status = (selectedJob.ado_sync?.status || '').toLowerCase();
    if (status !== 'syncing') return undefined;

    let active = true;
    const poll = async () => {
      try {
        const response = await apiClient.get<AdoSyncRun>(
          API_ENDPOINTS.PLAYWRIGHT_ADO_SYNC(projectId, selectedJob.id)
        );
        if (!active) return;
        setSelectedJobDetail(prev => prev ? { ...prev, ado_sync: response.data } : prev);
        setJobs(prev => prev.map(job => (
          job.id === selectedJob.id ? { ...job, ado_sync: response.data } : job
        )));
        if (isAdoSyncTerminal(response.data?.status)) {
          setAdoSyncing(false);
        }
      } catch {
        // Keep the current progress visible; the next poll can recover.
      }
    };

    void poll();
    const handle = window.setInterval(poll, 2000);
    return () => {
      active = false;
      window.clearInterval(handle);
    };
  }, [isAzureProject, projectId, selectedJob?.id, selectedJob?.ado_sync?.status]);

  function toggleExpand(jobId: number, e: React.MouseEvent) {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) { next.delete(jobId); } else { next.add(jobId); }
      return next;
    });
  }

  /* ── Filtered test results ── */
  // While the run is executing the final `summary` is absent; fall back to the
  // live per-test results streamed from execution checkpoints so tests appear as
  // they complete. Once the job finishes, `summary` takes over.
  const effectiveSummary = summary ?? selectedJob?.live_results ?? null;
  const allTests = useMemo(() => normalizeExecutionTests(effectiveSummary), [effectiveSummary]);

  const highlightedStatusFolders = useMemo(() => {
    const highlightedFolders = new Set<FolderFilter>();
    allTests.forEach((test) => {
      if (!test.test_case_key || !selectedTestKeys.has(test.test_case_key)) return;

      const status = executionStatus(test);
      if (status !== 'never_run') {
        highlightedFolders.add(status);
      }
    });
    return highlightedFolders;
  }, [allTests, selectedTestKeys]);

  const filteredTests = useMemo(() => {
    let list = allTests;

    // Folder filter
    if (folderFilter !== 'all') list = list.filter(t => executionStatus(t) === folderFilter);

    // Status filter dropdown
    if (statusFilter === 'passed' || statusFilter === 'failed' || statusFilter === 'partially_passed') {
      list = list.filter(t => executionStatus(t) === statusFilter);
    }

    // Search
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(t => {
        const hay = [t.title, t.name, t.test_case_key].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }

    return list;
  }, [allTests, folderFilter, statusFilter, search]);

  const allRunnableTestKeys = useMemo(
    () => allTests.map(t => t.test_case_key).filter((v): v is string => !!v),
    [allTests]
  );

  const selectedRunnableKeys = useMemo(
    () => Array.from(selectedTestKeys).filter(k => allRunnableTestKeys.includes(k)),
    [selectedTestKeys, allRunnableTestKeys]
  );

  const testsByKey = useMemo(() => {
    const map = new Map<string, ExecutionTestResult>();
    allTests.forEach(test => {
      if (test.test_case_key) map.set(test.test_case_key, test);
    });
    return map;
  }, [allTests]);

  const getVariantRunTests = useCallback((testKeys: string[]): VariantRunTest[] => (
    testKeys
      .map(testKey => {
        const test = testsByKey.get(testKey);
        if (!test) return null;
        const stats = getVariantStats(test);
        if (stats.total <= 0) return null;
        return {
          testKey,
          title: test.title || test.name || testKey,
          variantCount: stats.total,
        };
      })
      .filter((item): item is VariantRunTest => item !== null)
  ), [testsByKey]);

  const openRunDialogWithVariantConfirmation = useCallback((scope: RunDialogScope) => {
    if (!ensureTestExecutionStartAllowed()) {
      return;
    }

    const variantTests = getVariantRunTests(scope.testKeys);
    if (variantTests.length > 0) {
      setVariantRunConfirmation({
        action: 'dialog',
        title: scope.title,
        testKeys: scope.testKeys,
        artifactId: scope.artifactId,
        variantTests,
      });
      return;
    }
    setRunDialogScope(scope);
  }, [ensureTestExecutionStartAllowed, getVariantRunTests]);

  /* ── Direct re-run a single test without dialog (uses current job's base_url) ── */
  const rerunSingleTest = useCallback(async (testKey: string, options?: { confirmedVariants?: boolean }) => {
    if (!selectedJob?.artifact_id || !projectId) return;
    if (!ensureTestExecutionStartAllowed()) return;
    if (!options?.confirmedVariants) {
      const variantTests = getVariantRunTests([testKey]);
      if (variantTests.length > 0) {
        setVariantRunConfirmation({
          action: 'single',
          title: `Run: ${testKey}`,
          testKeys: [testKey],
          artifactId: selectedJob.artifact_id,
          singleTestKey: testKey,
          variantTests,
        });
        return;
      }
    }
    setRerunningKey(testKey);
    try {
      const res = await apiClient.post<{ rerun_job_id?: number }>(
        API_ENDPOINTS.PLAYWRIGHT_RERUN_TEST(projectId, selectedJob.id),
        {
          test_key: testKey,
          artifact_id: selectedJob.artifact_id,
          base_url: selectedJob.base_url ?? undefined,
        }
      );
      setRerunQueuedKeys(prev => new Set([...Array.from(prev), testKey]));
      // Use the rerun job ID to trigger polling (which updates left-panel counts when done).
      // Fall back to immediate selectJob refresh if no job ID is returned.
      if (res.data?.rerun_job_id) {
        setRerunInProgressJobId(res.data.rerun_job_id);
        setRerunInProgressKey(testKey);
      } else if (selectedJob) {
        await selectJob(selectedJob);
        setRerunQueuedKeys(prev => removeKeyFromSet(prev, testKey));
        setRerunTestKeys(prev => new Set([...Array.from(prev), testKey]));
      }
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail;
      const detailMessage = typeof rawDetail === 'string'
        ? rawDetail
        : err?.message || '';
      if (err?.response?.status === 403 && /not (enabled|active)/i.test(detailMessage)) {
        setExecutionActionError(detailMessage || TEST_EXECUTION_DISABLED_MESSAGE);
        return;
      }

      // Fall back to the full dialog for normal direct-run failures such as
      // missing cached credentials or base-url context.
      setRunDialogScope({
        title: `Run: ${testKey}`,
        testKeys: [testKey],
        artifactId: selectedJob.artifact_id,
      });
    } finally {
      setRerunningKey(null);
    }
  }, [ensureTestExecutionStartAllowed, getVariantRunTests, projectId, selectedJob, selectJob]);

  const confirmVariantRun = useCallback(() => {
    const confirmation = variantRunConfirmation;
    if (!confirmation) return;
    if (!ensureTestExecutionStartAllowed()) return;
    setVariantRunConfirmation(null);
    if (confirmation.action === 'single' && confirmation.singleTestKey) {
      void rerunSingleTest(confirmation.singleTestKey, { confirmedVariants: true });
      return;
    }
    setRunDialogScope({
      title: confirmation.title,
      testKeys: confirmation.testKeys,
      artifactId: confirmation.artifactId,
    });
  }, [ensureTestExecutionStartAllowed, rerunSingleTest, variantRunConfirmation]);

  const openRenameDialog = useCallback((job: PlaywrightJob) => {
    setRenameDialog({
      open: true,
      job,
      value: job.run_name || `Run #${job.id}`,
      saving: false,
      error: null,
    });
  }, []);

  const closeRenameDialog = useCallback(() => {
    setRenameDialog({ open: false, job: null, value: '', saving: false, error: null });
  }, []);

  const saveRenameDialog = useCallback(async () => {
    const job = renameDialog.job;
    const runName = renameDialog.value.trim();
    if (!job) return;
    if (!runName) {
      setRenameDialog(prev => ({ ...prev, error: 'Provide a run name.' }));
      return;
    }
    setRenameDialog(prev => ({ ...prev, saving: true, error: null }));
    try {
      const res = await apiClient.patch<PlaywrightJob>(
        API_ENDPOINTS.PLAYWRIGHT_RENAME_JOB(projectId, job.id),
        { run_name: runName },
      );
      const updatedJob = res.data;
      setJobs(prev => prev.map(item => (
        item.id === updatedJob.id ? { ...item, ...updatedJob } : item
      )));
      setSelectedJobDetail(prev => (
        prev && prev.id === updatedJob.id ? { ...prev, ...updatedJob } : prev
      ));
      closeRenameDialog();
    } catch (err: any) {
      setRenameDialog(prev => ({
        ...prev,
        saving: false,
        error: err?.response?.data?.detail || err?.message || 'Failed to rename the run.',
      }));
    }
  }, [closeRenameDialog, projectId, renameDialog.job, renameDialog.value]);

  const allVisibleSelected = useMemo(() => {
    const visibleKeys = filteredTests.map(t => t.test_case_key).filter((v): v is string => !!v);
    if (visibleKeys.length === 0) return false;
    return visibleKeys.every(k => selectedTestKeys.has(k));
  }, [filteredTests, selectedTestKeys]);

  /* ── Left tree: render a job node (same style as TestDirectoryTree) ── */
  function renderJobNode(job: PlaywrightJob): React.ReactNode {
    const isSelected = selectedJobId === job.id;
    const isExpanded = expandedIds.has(job.id);
    const passed = job.passed ?? 0;
    const failed = job.failed ?? 0;
    const partiallyPassed = job.partially_passed ?? 0;
    const hasResultCounts = passed > 0 || partiallyPassed > 0 || failed > 0;
    const status = (job.status || '').toLowerCase();
    const terminalStatusLabel =
      !hasResultCounts && status === 'failed' ? 'Failed' :
      !hasResultCounts && (status === 'cancelled' || status === 'canceled') ? 'Cancelled' :
      null;
    const activeFolderForThisJob = isSelected ? folderFilter : 'all';
    const isStatusFolderHighlighted = (status: FolderFilter): boolean => (
      isSelected && highlightedStatusFolders.has(status)
    );

    return (
      <div key={job.id} className="rrw-tree-node">
        <div
          className={`rrw-tree-row ${isSelected && activeFolderForThisJob === 'all' ? 'rrw-tree-row-selected' : ''}`}
          style={{ paddingLeft: '12px' }}
        >
          <button
            type="button"
            className="rrw-tree-toggle"
            onClick={(e) => toggleExpand(job.id, e)}
            aria-label={isExpanded ? 'Collapse run' : 'Expand run'}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          <button
            type="button"
            className="rrw-tree-select"
            onClick={() => { selectJob(job, 'all'); setExpandedIds(prev => { const n = new Set(prev); n.add(job.id); return n; }); }}
            onDoubleClick={(e) => {
              e.preventDefault();
              openRenameDialog(job);
            }}
          >
            {isExpanded ? <Folder2Open size={14} /> : <FolderFill size={14} />}
            <span className="rrw-tree-name">{job.run_name || `Run #${job.id}`}</span>
          </button>

          <button
            type="button"
            className="rrw-tree-action rrw-tree-rename"
            aria-label={`Rename run ${job.run_name || `Run #${job.id}`}`}
            title="Rename run"
            onClick={(e) => {
              e.stopPropagation();
              openRenameDialog(job);
            }}
          >
            <PencilFill size={11} />
          </button>

          <div className="rrw-tree-counts">
            {passed > 0 && <span className="rrw-tree-count rrw-tree-count-passed">{passed}</span>}
            {partiallyPassed > 0 && <span className="rrw-tree-count rrw-tree-count-partially-passed">{partiallyPassed}</span>}
            {failed > 0 && <span className="rrw-tree-count rrw-tree-count-failed">{failed}</span>}
            {terminalStatusLabel && (
              <span className={`execution-tree-status-badge execution-tree-status-badge--${status === 'failed' ? 'failed' : 'cancelled'}`}>
                {terminalStatusLabel}
              </span>
            )}
          </div>
        </div>

        {/* Sub-folders: Passed + Partially Passed + Failed */}
        {isExpanded && (
          <div className="rrw-tree-children">
            {passed > 0 && (
              <div className="rrw-tree-node">
                <div
                  className={`rrw-tree-row ${isSelected && activeFolderForThisJob === 'passed' ? 'rrw-tree-row-selected' : ''} ${isStatusFolderHighlighted('passed') ? 'rrw-tree-row-highlighted' : ''}`}
                  style={{ paddingLeft: '26px' }}
                >
                  <button type="button" className="rrw-tree-toggle" style={{ visibility: 'hidden' }}>
                    <span />
                  </button>
                  <button
                    type="button"
                    className="rrw-tree-select"
                    onClick={() => selectJob(job, 'passed')}
                  >
                    <Folder2Open size={13} style={{ color: '#0f766e' }} />
                    <span className="rrw-tree-name">Passed</span>
                  </button>
                  <div className="rrw-tree-counts">
                    <span className="rrw-tree-count rrw-tree-count-passed">{passed}</span>
                  </div>
                </div>
              </div>
            )}
            {partiallyPassed > 0 && (
              <div className="rrw-tree-node">
                <div
                  className={`rrw-tree-row ${isSelected && activeFolderForThisJob === 'partially_passed' ? 'rrw-tree-row-selected' : ''} ${isStatusFolderHighlighted('partially_passed') ? 'rrw-tree-row-highlighted' : ''}`}
                  style={{ paddingLeft: '26px' }}
                >
                  <button type="button" className="rrw-tree-toggle" style={{ visibility: 'hidden' }}><span /></button>
                  <button type="button" className="rrw-tree-select" onClick={() => selectJob(job, 'partially_passed')}>
                    <FolderFill size={13} style={{ color: '#f97316' }} />
                    <span className="rrw-tree-name">Partially Passed</span>
                  </button>
                  <div className="rrw-tree-counts">
                    <span className="rrw-tree-count rrw-tree-count-partially-passed">{partiallyPassed}</span>
                  </div>
                </div>
              </div>
            )}
            {failed > 0 && (
              <div className="rrw-tree-node">
                <div
                  className={`rrw-tree-row ${isSelected && activeFolderForThisJob === 'failed' ? 'rrw-tree-row-selected' : ''} ${isStatusFolderHighlighted('failed') ? 'rrw-tree-row-highlighted' : ''}`}
                  style={{ paddingLeft: '26px' }}
                >
                  <button type="button" className="rrw-tree-toggle" style={{ visibility: 'hidden' }}>
                    <span />
                  </button>
                  <button
                    type="button"
                    className="rrw-tree-select"
                    onClick={() => selectJob(job, 'failed')}
                  >
                    <FolderFill size={13} style={{ color: '#b91c1c' }} />
                    <span className="rrw-tree-name">Failed</span>
                  </button>
                  <div className="rrw-tree-counts">
                    <span className="rrw-tree-count rrw-tree-count-failed">{failed}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ── Right panel: test result cards ── */
  function renderTestCard(test: ExecutionTestResult, idx: number): React.ReactNode {
    const { pill, label } = getCardStatus(test);
    const title = test.title || test.name || test.test_case_key || `Test ${idx + 1}`;
    const durationStr = test.duration_ms != null ? `${(test.duration_ms / 1000).toFixed(1)}s` : null;
    const artifactId = selectedJob?.artifact_id ?? null;
    const testKey = test.test_case_key || null;
    const isChecked = !!testKey && selectedTestKeys.has(testKey);
    const isRerunQueued = keySetHasTestOrVariant(rerunQueuedKeys, testKey);
    const isRerunning = rerunningKey === testKey;
    const isRerunActive = isRerunQueued || isRerunning;
    const isActionsEdited = keySetHasTestOrVariant(editedTestKeys, testKey);
    const actionUpdates = testKey
      ? (() => {
        const variantEntries = Object.entries(actionUpdateSummaries)
          .filter(([key]) => key.startsWith(`${testKey}::`))
          .flatMap(([, summaries]) => summaries || []);
        if (variantEntries.length > 0) return variantEntries;
        return Object.entries(actionUpdateSummaries)
          .filter(([key]) => key === testKey)
          .flatMap(([, summaries]) => summaries || []);
    })()
      : [];
    const actionUpdateLabel = formatActionUpdateSummary(actionUpdates);
    const isReran = !isRerunActive && keySetHasTestOrVariant(rerunTestKeys, testKey);
    const failureVersions = failureClassificationSummary(test, failureClassifications);
    const scriptFailureCount = failureVersions.reduce((total, version) => total + version.scriptVariants.length, 0);
    const applicationFailureCount = failureVersions.reduce((total, version) => total + version.applicationVariants.length, 0);
    const failureBadges = (scriptFailureCount > 0 || applicationFailureCount > 0) && (
      <div style={{ flexBasis: '100%', minHeight: 20, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6 }}>
        {scriptFailureCount > 0 && (
          <OverlayTrigger
            placement="bottom"
            trigger={['hover', 'focus', 'click']}
            overlay={(
              <Tooltip id={`script-issue-${testKey || idx}`}>
                <div style={{ textAlign: 'left', minWidth: 220 }}>
                  <strong>Script Issue</strong>
                  {failureVersions.filter(version => version.scriptVariants.length > 0).map(version => (
                    <div key={version.versionLabel}>{version.versionLabel}: {version.scriptVariants.join(', ')}</div>
                  ))}
                </div>
              </Tooltip>
            )}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
              style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', cursor: 'pointer',
                background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}
            >
              Script Issue ({scriptFailureCount})
            </span>
          </OverlayTrigger>
        )}
        {applicationFailureCount > 0 && (
          <OverlayTrigger
            placement="bottom"
            trigger={['hover', 'focus', 'click']}
            overlay={(
              <Tooltip id={`app-failure-${testKey || idx}`}>
                <div style={{ textAlign: 'left', minWidth: 220 }}>
                  <strong>App Failure</strong>
                  {failureVersions.filter(version => version.applicationVariants.length > 0).map(version => (
                    <div key={version.versionLabel}>{version.versionLabel}: {version.applicationVariants.join(', ')}</div>
                  ))}
                </div>
              </Tooltip>
            )}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
              style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', cursor: 'pointer',
                background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}
            >
              App Failure ({applicationFailureCount})
            </span>
          </OverlayTrigger>
        )}
      </div>
    );
    const variantStats = getVariantStats(test);
    const variantCount = variantStats.total;
    const variantMeta = variantCount > 0 ? formatVariantStatus(variantStats).toLowerCase() : null;
    const selectedJobIsUnsuccessful = isUnsuccessfulExecutionStatus(selectedJob?.status);

    return (
      <div
        key={idx}
        className="rrw-test-card"
        onClick={() => setPreviewTest(test)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setPreviewTest(test); }}
      >
        <div className="rrw-test-card-header" style={{ alignItems: 'flex-start' }}>
          <div className="rrw-test-card-leading" style={{ flex: '1 1 0', minWidth: 0 }}>
            <Form.Check
              className="rrw-test-card-check"
              type="checkbox"
              checked={isChecked}
              disabled={selectedJobIsUnsuccessful}
              onClick={e => e.stopPropagation()}
              onChange={() => {
                if (!testKey || selectedJobIsUnsuccessful) return;
                setSelectedTestKeys(prev => {
                  const next = new Set(prev);
                  if (next.has(testKey)) next.delete(testKey);
                  else next.add(testKey);
                  return next;
                });
              }}
              aria-label={`Select ${title}`}
            />
            <div className="rrw-test-card-copy">
              <div className="rrw-test-card-title">{title}</div>
              {test.test_case_key && (
                <div className="rrw-test-card-path">{test.test_case_key}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flex: '0 0 480px', minHeight: 52, flexWrap: 'wrap', alignContent: 'flex-start', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginLeft: 12 }}>
            {isRerunActive && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px',
                background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}>Running</span>
            )}
            {isActionsEdited && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px',
                background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}>Actions Updated</span>
            )}
            {actionUpdateLabel && (
              <span className="execution-action-update-pill">
                {actionUpdateLabel}
              </span>
            )}
            {variantCount > 0 && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px',
                background: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}>{variantCount} variants</span>
            )}
            {isReran && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px',
                background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                borderRadius: 12, whiteSpace: 'nowrap',
              }}>Re-ran</span>
            )}
            <div className={`rrw-status-pill rrw-status-pill-${pill}`}>{label}</div>
            {failureBadges}
          </div>
        </div>

        {(durationStr || variantMeta) && (
          <div className="rrw-test-card-meta">
            {durationStr && <span>Duration: {durationStr}</span>}
            {variantCount > 0 && (
              <span>Variants: {variantMeta}</span>
            )}
          </div>
        )}

        <div className="rrw-test-card-footer">
          <div className="rrw-test-card-run">
            {selectedJob?.base_url || ''}
          </div>
          <div className="rrw-inline-actions">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={e => { e.stopPropagation(); setPreviewTest(test); }}
            >
              <EyeFill size={12} className="me-1" />
              Preview
            </Button>
            {test.test_case_key && artifactId && (
              <Button
                variant="primary"
                size="sm"
                disabled={isRerunActive || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                onClick={e => {
                  e.stopPropagation();
                  if (selectedJobIsUnsuccessful || testExecutionActionDisabled) return;
                  void rerunSingleTest(test.test_case_key!);
                }}
              >
                {isRerunActive
                  ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />Running…</>
                  : <><PlayFill size={12} className="me-1" />Run</>
                }
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── Main render ── */
  const selectedJobIsUnsuccessful = isUnsuccessfulExecutionStatus(selectedJob?.status);
  const hasActiveJob = jobs.some(j => {
    const status = (j.status || '').toLowerCase();
    return status === 'running' || status === 'pending';
  });
  const selectedJobIsActive = (() => {
    const status = (selectedJob?.status || '').toLowerCase();
    return status === 'running' || status === 'pending';
  })();
  const selectedJobIsCompleted = (selectedJob?.status || '').toLowerCase() === 'completed';
  const selectedJobElapsedMs = jobElapsedDurationMs(selectedJob);
  const hasVariantResults = allTests.some(test => getVariantStats(test).total > 0);
  const adoSync = selectedJob?.ado_sync ?? null;
  const adoSyncStatus = adoSync?.status || 'not_synced';
  const adoSyncInProgress = adoSyncStatus === 'syncing';
  const adoSyncPreviouslyAttempted = !!adoSync && adoSyncStatus !== 'not_synced';
  const adoSyncButtonLabel = adoSyncInProgress
    ? 'Syncing...'
    : adoSyncPreviouslyAttempted
      ? 'Re-sync Azure Test Plans'
      : 'Sync to Azure Test Plans';
  const adoSyncStatusLabel = adoSyncStatus.replace(/_/g, ' ');
  const adoSyncProgress = getAdoSyncProgress(adoSync);
  const adoSyncHasProgress = !!adoSync && ((adoSync.total_count ?? 0) > 0 || adoSyncInProgress);
  const adoSyncPanelTitle = adoSyncInProgress
    ? 'Azure Test Plans sync in progress'
    : `Azure Test Plans sync ${adoSyncStatusLabel}`;
  const freshRunDisabled = rerunAllLoading || !selectedJob?.artifact_id || hasActiveJob || testExecutionActionDisabled;
  const freshRunButton = selectedJob ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id="tip-fresh">Ignores all pre-generated scripts. Every test goes through the full AI pipeline from scratch.</Tooltip>}>
      <span className="d-inline-block">
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={freshRunDisabled}
          style={freshRunDisabled ? { pointerEvents: 'none' } : {}}
          onClick={() => { setRerunMode('fresh'); setRerunNameValue(`Fresh Run ${new Date().toLocaleDateString()}`); setFreshRunDialog(true); }}
        >
          Fresh Run
        </Button>
      </span>
    </OverlayTrigger>
  ) : null;

  return (
    <div className="rrw-page rrw-page-embedded">
      <div className="rrw-metrics" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
        <div className="rrw-metric-card">
          <div className="rrw-metric-label">Version</div>
          <div className="rrw-metric-value">{formatVersion(selectedJob?.source_version_number)}</div>
          <div className="rrw-metric-note">Source test catalog</div>
        </div>
        <div className="rrw-metric-card">
          <div className="rrw-metric-label">{hasVariantResults ? 'Total Variant Runs' : 'Total Tests'}</div>
          <div className="rrw-metric-value">
            {loadingSummary && allTests.length === 0
              ? <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Loading…</span>
              : effectiveSummary?.total_tests ?? selectedJob?.total_tests ?? 0}
          </div>
          <div className="rrw-metric-note">{hasVariantResults ? 'Variant executions in this run' : 'Included in this execution'}</div>
        </div>
        <div className="rrw-metric-card">
          <div className="rrw-metric-label">Run Time</div>
          <div className="rrw-metric-value">
            {selectedJobIsActive
              ? <span style={{ fontSize: '0.85rem', color: '#2563eb' }}>In progress…</span>
              : formatDuration(selectedJobElapsedMs)}
          </div>
          <div className="rrw-metric-note">Elapsed job duration</div>
        </div>
        <div className="rrw-metric-card">
          <div className="rrw-metric-label">Date</div>
          <div className="rrw-metric-value" style={{ fontSize: '0.95rem' }}>{formatDate(selectedJob?.completed_at ?? selectedJob?.created_at ?? null)}</div>
          <div className="rrw-metric-note">Most recent execution timestamp</div>
        </div>
      </div>

      <div className="rrw-workspace">

        {/* ── Left: runs directory ── */}
        <div className="rrw-column">
          <div className="rrw-panel">
            <div className="rrw-panel-header">
              <h3 className="rrw-panel-title">Runs</h3>
              <button
                type="button"
                onClick={loadJobs}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#66788f', padding: '4px' }}
                title="Refresh"
              >
                <ArrowClockwise size={14} />
              </button>
            </div>

            {loadingJobs && (
              <div className="rrw-loading" style={{ minHeight: 80 }}>
                <Spinner animation="border" size="sm" />
              </div>
            )}
            {!loadingJobs && jobsError && (
              <div className="rrw-empty-panel">{jobsError}</div>
            )}
            {!loadingJobs && !jobsError && jobs.length === 0 && (
              <div className="rrw-empty-panel">
                No Playwright runs yet. Launch a run from the workspace.
              </div>
            )}
            {!loadingJobs && jobs.length > 0 && (
              <div className="rrw-tree">
                {jobs.map(renderJobNode)}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: test results panel ── */}
        <div className="rrw-column">
          <div className="rrw-panel rrw-results-panel">

            {!selectedJob ? (
              startingRun ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 14, padding: '48px 24px', textAlign: 'center', minHeight: 200,
                }}>
                  <Spinner animation="border" style={{ color: '#2563eb', width: 32, height: 32 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>Starting run…</div>
                    <div style={{ fontSize: '0.83rem', color: '#64748b', marginTop: 6 }}>
                      The pipeline is initialising. This usually takes 20–40 seconds.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rrw-empty-panel" style={{ minHeight: 200 }}>
                  Select a run from the left panel to view test results.
                </div>
              )
            ) : (
              <>
                <div className="rrw-panel-header">
                  <div className="execution-selected-run-title">
                    <h3 className="rrw-panel-title">
                      {selectedJob.run_name || `Run #${selectedJob.id}`}
                    </h3>
                    {selectedJob.run_config && (
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tip-run-config">Run configuration</Tooltip>}>
                        <button
                          type="button"
                          className="execution-run-config-btn"
                          onClick={() => setShowRunConfig(true)}
                          aria-label="View run configuration"
                        >
                          <InfoCircle size={15} />
                        </button>
                      </OverlayTrigger>
                    )}
                  </div>
                  <p className="rrw-panel-subtitle">
                    {filteredTests.length} test{filteredTests.length === 1 ? '' : 's'} in view
                    {summary ? ` • ${summary.passed ?? 0} passed, ${summary.failed ?? 0} failed` : ''}
                  </p>
                </div>

                {rerunInProgressJobId && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', margin: '0 0 8px 0',
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                    fontSize: '0.85rem', color: '#1d4ed8',
                  }}>
                    <Spinner animation="border" size="sm" style={{ color: '#2563eb', flexShrink: 0 }} />
                    <span>Testcase execution rerun is running. Results will refresh automatically when complete.</span>
                  </div>
                )}

                {executionActionError && (
                  <Alert variant="warning" className="mb-2">
                    {executionActionError}
                  </Alert>
                )}

                {isAzureProject && (
                <div className="execution-ado-sync-panel">
                  <div className="execution-ado-sync-copy">
                    <div className="execution-ado-sync-title">Azure Test Plans Sync</div>
                    <div className="execution-ado-sync-meta">
                      Status: <strong>{adoSyncStatusLabel}</strong>
                      {adoSync?.ado_test_run_id ? <> · ADO Run #{adoSync.ado_test_run_id}</> : null}
                    </div>
                  </div>
                  <div className="execution-ado-sync-actions">
                    {adoSync?.ado_run_url && (
                      <Button
                        as="a"
                        href={adoSync.ado_run_url}
                        target="_blank"
                        rel="noreferrer"
                        variant="outline-primary"
                        size="sm"
                      >
                        Open ADO Run
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void handleAdoSync()}
                      disabled={!selectedJobIsCompleted || adoSyncing || adoSyncInProgress}
                    >
                      {adoSyncing || adoSyncInProgress
                        ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />Syncing...</>
                        : adoSyncButtonLabel}
                    </Button>
                  </div>
                  {!selectedJobIsCompleted && (
                    <Alert variant="info" className="execution-ado-sync-alert">
                      Azure Test Plans sync is available after this Playwright execution completes.
                    </Alert>
                  )}
                  {adoSyncError && (
                    <Alert variant="danger" className="execution-ado-sync-alert">{adoSyncError}</Alert>
                  )}
                  {adoSync?.error_message && (
                    <Alert variant="danger" className="execution-ado-sync-alert">{adoSync.error_message}</Alert>
                  )}
                  {adoSyncHasProgress && (
                    <div className={`execution-ado-sync-progress execution-ado-sync-progress--${adoSyncStatus}`}>
                      <div className="execution-ado-sync-progress-head">
                        <div>
                          <div className="execution-ado-sync-progress-title">{adoSyncPanelTitle}</div>
                          <div className="execution-ado-sync-progress-message">
                            {adoSync?.message
                              || adoSync?.current_action
                              || (adoSyncStatus === 'syncing' ? 'Syncing Playwright results with Azure Test Plans...' : 'Azure Test Plans sync finished.')}
                          </div>
                          {(adoSync?.current_test_key || adoSync?.current_action) && adoSyncStatus === 'syncing' && (
                            <div className="execution-ado-sync-progress-current">
                              {adoSync.current_action ? `${adoSync.current_action}` : 'Processing'}
                              {adoSync.current_test_key ? ` · ${adoSync.current_test_key}` : ''}
                            </div>
                          )}
                        </div>
                        <strong>{adoSync?.processed_count ?? 0} / {adoSync?.total_count ?? 0}</strong>
                      </div>
                      <div className="execution-ado-sync-progress-track" aria-hidden="true">
                        <div
                          className="execution-ado-sync-progress-fill"
                          style={{ width: `${adoSyncProgress}%` }}
                        />
                      </div>
                      <div className="execution-ado-sync-progress-meta">
                        <span>{adoSync?.synced_count ?? 0} synced</span>
                        <span>{adoSync?.skipped_count ?? 0} skipped</span>
                        <span>{adoSync?.failed_count ?? 0} failed</span>
                      </div>
                    </div>
                  )}
                  {adoSync?.results && adoSync.results.length > 0 && (
                    <div className="execution-ado-sync-results">
                      {adoSync.results.map(result => (
                        <div className="execution-ado-sync-result" key={result.test_key}>
                          <span>
                            <strong>{result.test_key}</strong>
                            {result.message ? <span className="execution-ado-sync-result-message"> - {result.message}</span> : null}
                          </span>
                          <span className={`execution-ado-sync-result-pill execution-ado-sync-result-pill--${result.status}`}>
                            {result.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                <div className="rrw-inline-actions">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      const visibleKeys = filteredTests
                        .map(t => t.test_case_key)
                        .filter((v): v is string => !!v);
                      setSelectedTestKeys(prev => {
                        const next = new Set(prev);
                        if (visibleKeys.length > 0 && visibleKeys.every(k => next.has(k))) {
                          visibleKeys.forEach(k => next.delete(k));
                        } else {
                          visibleKeys.forEach(k => next.add(k));
                        }
                        return next;
                      });
                    }}
                    disabled={filteredTests.length === 0 || selectedJobIsUnsuccessful}
                  >
                    {allVisibleSelected ? 'Clear All' : 'Select All'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedTestKeys(new Set())}
                    disabled={selectedRunnableKeys.length === 0 || selectedJobIsUnsuccessful}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => openRunDialogWithVariantConfirmation({
                      title: 'Run selected tests',
                      testKeys: selectedRunnableKeys,
                      artifactId: selectedJob.artifact_id ?? null,
                    })}
                    disabled={selectedRunnableKeys.length === 0 || !selectedJob.artifact_id || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                  >
                    <PlayFill size={12} className="me-1" />
                    Run Selected
                  </Button>
                  {jobs.length > 0 ? (
                    <>
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tip-rerun">Replays all tests using pre-generated scripts only - zero AI calls, fastest option.</Tooltip>}>
                        <span className="d-inline-block">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                            style={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled ? { pointerEvents: 'none' } : {}}
                            onClick={() => { setRerunMode('rerun'); setRerunNameValue(`Re-run ${new Date().toLocaleDateString()}`); setRerunNameDialog(true); }}
                          >
                            <PlayFill size={12} className="me-1" />Re-run
                          </Button>
                        </span>
                      </OverlayTrigger>
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tip-smart">Re-runs all tests. Passed/unchanged tests use pre-generated scripts; failed or edited tests re-resolve via AI.</Tooltip>}>
                        <span className="d-inline-block">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                            style={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled ? { pointerEvents: 'none' } : {}}
                            onClick={() => { setRerunMode('smart'); setRerunNameValue(`Smart Run ${new Date().toLocaleDateString()}`); setRerunNameDialog(true); }}
                          >
                            Smart Run
                          </Button>
                        </span>
                      </OverlayTrigger>
                      {freshRunButton}
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tip-failed">Only re-runs tests that failed last time. Passing tests are skipped.</Tooltip>}>
                        <span className="d-inline-block">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                            style={rerunAllLoading || !selectedJob.artifact_id || hasActiveJob || selectedJobIsUnsuccessful || testExecutionActionDisabled ? { pointerEvents: 'none' } : {}}
                            onClick={() => { setRerunMode('failed_only'); setRerunNameValue(`Failed Only ${new Date().toLocaleDateString()}`); setRerunNameDialog(true); }}
                          >
                            Failed Only
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openRunDialogWithVariantConfirmation({
                        title: 'Run all tests',
                        testKeys: allRunnableTestKeys,
                        artifactId: selectedJob.artifact_id ?? null,
                      })}
                      disabled={allRunnableTestKeys.length === 0 || !selectedJob.artifact_id || selectedJobIsUnsuccessful || testExecutionActionDisabled}
                    >
                      <PlayFill size={12} className="me-1" />
                      Run All
                    </Button>
                  )}
                </div>

                <div
                  className="rrw-filter-bar"
                  style={{ gridTemplateColumns: 'minmax(0,1fr) 170px' }}
                >
                  <div className="rrw-search">
                    <Search size={13} className="rrw-search-icon" />
                    <input
                      className="rrw-search-input"
                      placeholder="Search tests…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <Form.Select
                    className="rrw-filter-select"
                    size="sm"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All statuses</option>
                <option value="passed">Passed</option>
                <option value="partially_passed">Partially passed</option>
                <option value="failed">Failed</option>
                  </Form.Select>
                </div>

                {/* Show spinner only while we have nothing to display yet — i.e. still
                     loading, or an active run whose live results haven't streamed in.
                     Once live (or final) results exist, render them instead of blocking
                     on the final summary (which only lands at completion). */}
                {((loadingSummary || (!effectiveSummary && selectedJobIsActive)) && allTests.length === 0) ? (
                  <div className="rrw-loading" style={{ minHeight: 120 }}>
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <div className="rrw-results-list">
                    {filteredTests.length === 0 && (selectedJob?.status === 'running' || selectedJob?.status === 'pending') ? (
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 12, padding: '40px 24px', textAlign: 'center',
                      }}>
                        <Spinner animation="border" style={{ color: '#2563eb', width: 28, height: 28 }} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>Run in progress</div>
                          <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 4 }}>
                            Tests are currently executing. Results will appear here once the run completes.
                          </div>
                        </div>
                      </div>
                    ) : filteredTests.length === 0 ? (
                      <div className="rrw-empty-panel">No tests match the current filter.</div>
                    ) : (
                      filteredTests.map((t, i) => renderTestCard(t, i))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {variantRunConfirmation && (
        <Modal show onHide={() => setVariantRunConfirmation(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1rem' }}>Synthetic Data Variants Found</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(() => {
              const testCount = variantRunConfirmation.variantTests.length;
              const totalVariants = variantRunConfirmation.variantTests.reduce((sum, item) => sum + item.variantCount, 0);
              const isSingle = variantRunConfirmation.action === 'single';
              return (
                <div className="d-flex flex-column gap-3">
                  <div style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.5 }}>
                    {testCount === 1 ? (
                      <>
                        This test case has <strong>{totalVariants}</strong> synthetic data variant{totalVariants === 1 ? '' : 's'}.
                        {' '}{isSingle ? 'Running it will execute all variants and update the result card.' : 'Continue to the run setup to choose how to use this data.'}
                      </>
                    ) : (
                      <>
                        <strong>{testCount}</strong> selected test cases have synthetic data variants, with <strong>{totalVariants}</strong> variant execution{totalVariants === 1 ? '' : 's'} available.
                        {' '}Continue to the run setup to choose how to use this data.
                      </>
                    )}
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    {variantRunConfirmation.variantTests.slice(0, 5).map(item => (
                      <div
                        key={item.testKey}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '8px 10px',
                          borderBottom: '1px solid #e2e8f0',
                          fontSize: '0.82rem',
                        }}
                      >
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                        <span style={{ color: '#0e7490', fontWeight: 700, whiteSpace: 'nowrap' }}>{item.variantCount} variants</span>
                      </div>
                    ))}
                    {variantRunConfirmation.variantTests.length > 5 && (
                      <div style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#64748b' }}>
                        +{variantRunConfirmation.variantTests.length - 5} more test case{variantRunConfirmation.variantTests.length - 5 === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={() => setVariantRunConfirmation(null)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={confirmVariantRun}>
              {variantRunConfirmation.action === 'single' ? 'Run All Variants' : 'Continue'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ── Run dialog ── */}
      {runDialogScope && (
        <ScriptArgumentsDialog
          show
          onHide={() => setRunDialogScope(null)}
          projectId={projectId}
          sourceJobId={selectedJob?.id ?? undefined}
          artifactId={runDialogScope.artifactId ?? undefined}
          testKeys={runDialogScope.testKeys}
          title={runDialogScope.title}
          onRunStarted={() => { setRunDialogScope(null); loadJobs(); }}
        />
      )}

      <RunConfigModal
        show={showRunConfig}
        onHide={() => setShowRunConfig(false)}
        runConfig={selectedJob?.run_config}
        fallbackBaseUrl={selectedJob?.base_url}
        runName={selectedJob?.run_name}
        jobId={selectedJob?.id}
      />

      {/* ── Fresh Run dialog — full credentials popup ── */}
      <ScriptArgumentsDialog
        show={freshRunDialog}
        onHide={() => setFreshRunDialog(false)}
        projectId={projectId}
        sourceJobId={selectedJob?.id ?? undefined}
        artifactId={selectedJob?.artifact_id ?? undefined}
        defaultRunMode="fresh"
        title="Fresh Run"
        onRunStarted={() => {
          setFreshRunDialog(false);
          setStartingRun(true);
          setSelectedJobId(null);
          setSelectedJobDetail(null);
          setSummary(null);
          loadJobs();
        }}
      />

      {/* ── Preview popup ── */}
      {previewTest && selectedJob && (
        <ExecutionTestPopup
          test={previewTest}
          runMeta={{
            run_name: selectedJob.run_name,
            base_url: selectedJob.base_url,
            created_at: selectedJob.created_at,
            status: selectedJob.status,
            total_tests: summary?.total_tests,
            passed: summary?.passed,
            failed: summary?.failed,
            pass_rate: summary?.pass_rate,
            project_id: projectId,
            job_id: selectedJob.id,
            run_id: selectedJob.run_id,
            artifact_id: selectedJob.artifact_id ?? null,
          }}
          onClose={() => setPreviewTest(null)}
          onRunStarted={(rerunJobId?: number, rerunKey?: string) => {
            setPreviewTest(null);
            if (rerunJobId) {
              setRerunInProgressJobId(rerunJobId);
              setRerunInProgressKey(rerunKey ?? null);
            } else if (rerunKey) {
              setRerunQueuedKeys(prev => removeKeyFromSet(prev, rerunKey));
              setRerunTestKeys(prev => new Set([...Array.from(prev), rerunKey]));
            }
          }}
          onTestRerun={(key) => {
            setRerunQueuedKeys(prev => new Set([...Array.from(prev), key]));
          }}
          onActionsUpdated={(key, summaries, allSummaries) => {
            setEditedTestKeys(prev => new Set([...Array.from(prev), key]));
            setActionUpdateSummaries(prev => ({ ...prev, ...(allSummaries || {}), [key]: summaries }));
          }}
        />
      )}

      {/* ── Rename execution run dialog ── */}
      <Modal show={renameDialog.open} onHide={closeRenameDialog} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Rename Run</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: '0.85rem', color: '#66788f', marginBottom: 8 }}>
            Rename {renameDialog.job?.run_name || (renameDialog.job ? `Run #${renameDialog.job.id}` : 'this run')}.
          </div>
          <Form.Control
            autoFocus
            type="text"
            placeholder="Run name"
            value={renameDialog.value}
            disabled={renameDialog.saving}
            onChange={e => setRenameDialog(prev => ({ ...prev, value: e.target.value, error: null }))}
            onKeyDown={e => { if (e.key === 'Enter') void saveRenameDialog(); }}
          />
          {renameDialog.error && (
            <div className="rrw-modal-error" style={{ color: 'var(--rrw-danger)', marginTop: 8, fontSize: '0.85rem' }}>
              {renameDialog.error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeRenameDialog} disabled={renameDialog.saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={renameDialog.saving || !renameDialog.value.trim()}
            onClick={() => void saveRenameDialog()}
          >
            {renameDialog.saving ? <Spinner animation="border" size="sm" /> : 'Rename Run'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Run name dialog ── */}
      <Modal show={rerunNameDialog} onHide={() => setRerunNameDialog(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {{ smart: 'Smart Run', fresh: 'Fresh Run', failed_only: 'Failed Only', rerun: 'Re-run' }[rerunMode] ?? 'Run'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            autoFocus
            type="text"
            placeholder="Run name"
            value={rerunNameValue}
            onChange={e => setRerunNameValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.form?.requestSubmit(); }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setRerunNameDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={rerunAllLoading || !rerunNameValue.trim() || testExecutionActionDisabled}
            onClick={async () => {
              if (!selectedJob?.artifact_id || !projectId) return;
              if (!ensureTestExecutionStartAllowed()) return;
              const sourceBaseUrl = selectedJob.base_url ?? '';
              if (!sourceBaseUrl) { alert('No base URL found for the selected run.'); return; }
              setRerunAllLoading(true);
              setRerunNameDialog(false);
              try {
                await apiClient.post(API_ENDPOINTS.PLAYWRIGHT_CREATE_JOB(projectId), {
                  project_id: projectId,
                  source_job_id: selectedJob.id,
                  artifact_id: selectedJob.artifact_id,
                  run_mode: rerunMode,
                  run_name: rerunNameValue.trim(),
                  base_url: sourceBaseUrl,
                });
                // Show "starting" banner immediately — backend takes 30-50s to spin up
                setStartingRun(true);
                // Reset selection so auto-select picks the new running job
                setSelectedJobId(null);
                setSelectedJobDetail(null);
                setSummary(null);
                loadJobs();
              } catch (err: any) {
                const msg = err?.response?.data?.detail || err?.message || 'Failed to start run';
                alert(msg);
              } finally {
                setRerunAllLoading(false);
              }
            }}
          >
            {rerunAllLoading ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />Starting…</> : 'Start'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
